<script setup lang="ts">
import { bemBuilder } from "@chatfood/core-utils";
import { onMounted, onBeforeUnmount, ref } from "vue";
import { isOrderRepoError } from "@chatfood/dbe-client/errors";
import { useModalHubStore } from "../modal-hub";
import { CHECKOUT_CALLBACK_PARAM } from "~/domain/checkout-callback-param";
import type { IDomainOrderStatus } from "~/domain/order-status";
import { useLocale } from "~/composables/use-locale";
import { useNavigation } from "~/composables/use-navigation";
import { useBffErrors } from "~/composables/use-bff-errors";
import { useAnalytics, useBugReporter, useDirectBackend } from "#imports";
import { useBasketStore } from "~/stores/basket";

const css = bemBuilder("checkout-callback");

const { t } = useLocale();
const { route, push, resolvePath } = useNavigation();
const { errorsToString } = useBffErrors();
const { report } = useBugReporter();
const { trackEvent } = useAnalytics();
const { orderRepo } = useDirectBackend();

const modalHub = useModalHubStore();
const basket = useBasketStore();

const statusToFail: IDomainOrderStatus[] = ["unpaid"];
const statusToRetry: IDomainOrderStatus[] = ["created"];
const MAX_RETRY_ATTEMPTS = 60 * 5; // 5 minutes
const RETRY_INTERVAL = 1000; // 1 second

const orderUid = ref();
const orderStatus = ref();
const orderTrackingId = ref();
let timeoutId: NodeJS.Timeout | null = null;

function orderTrackingUrl(trackingId: string): string {
  try {
    return resolvePath("order-tracking", { trackingId });
  } catch {
    // This navigation is the only way to hand a paying customer their order,
    // so if router resolution itself is broken, fall back to the literal path.
    return `/orders/${encodeURIComponent(trackingId)}`;
  }
}

function isTransientPollingError(error: unknown): boolean {
  // The repo's "unknown" code wraps 5xx and network failures; every other
  // code (notFound, unauthorized, tooManyRequests, validation, businessRule)
  // is deterministic and will not heal on retry.
  if (isOrderRepoError(error)) {
    return error.isUnknown();
  }

  // Structured BFF business errors are deterministic too; anything else is
  // unexpected and worth retrying.
  return !errorsToString(error, "\n");
}

function showGenericError() {
  modalHub.showError(
    t("components.checkout_callback.unknown_error_title"),
    t("components.checkout_callback.unknown_error_message"),
    t("components.checkout_callback.unknown_error_cta"),
  );
}

async function checkOrderStatus(orderId: string, attemptCount = 0) {
  try {
    const res = await orderRepo.getById({ orderId });

    if (!res) {
      throw new Error("Response from server is empty.");
    }

    orderUid.value = res.uid;
    orderStatus.value = res.status;
    orderTrackingId.value = res.trackingId;

    if (statusToRetry.includes(orderStatus.value)) {
      if (attemptCount >= MAX_RETRY_ATTEMPTS) {
        // Too many attempts, treat as timeout
        modalHub.hideLoading();
        modalHub.showError(
          t("components.checkout_callback.attempt_error_title"),
          t("components.checkout_callback.attempt_error_message"),
          t("components.checkout_callback.attempt_error_cta"),
        );
        return;
      }

      timeoutId = setTimeout(
        () => checkOrderStatus(orderId, attemptCount + 1),
        RETRY_INTERVAL,
      );
      return;
    }

    if (statusToFail.includes(orderStatus.value)) {
      modalHub.hideLoading();

      trackEvent({
        name: "orderFailure",
        payload: {
          orderId,
          orderUid: orderUid.value,
          reason: orderStatus.value,
        },
      });

      modalHub.showError(
        t("components.checkout_callback.payment_failed_title"),
        t("components.checkout_callback.payment_failed_message"),
        t("components.checkout_callback.payment_failed_cta"),
      );
      return;
    }

    trackEvent({
      name: "orderSuccess",
      payload: {
        orderId,
        orderUid: orderUid.value,
      },
    });

    try {
      await push("order-tracking", { trackingId: orderTrackingId.value });

      modalHub.hideLoading();
      basket.removeBasket();
    } catch (navError) {
      report("Failed to navigate to order tracking after successful order", {
        context: {
          navError:
            navError instanceof Error ? navError.message : String(navError),
        },
        tags: { "order.id": orderId, "code.flag": "checkout-nav-fallback" },
      });

      // The order is confirmed at this point, so a failure here is only about
      // reaching the tracking page (e.g. a lazy route/middleware chunk that
      // WebKit keeps cached as broken after a flaky fetch). A full page load
      // refetches every asset and recovers where router.push cannot, so keep
      // the loading modal up until the browser unloads the page.
      try {
        await basket.removeBasket();
      } catch {
        // Best effort: the basket must not block handing over the order page.
      }
      window.location.assign(orderTrackingUrl(orderTrackingId.value));
    }
  } catch (error: unknown) {
    // A transient failure (network blip, 5xx) should not end five minutes of
    // patience right after a successful payment — keep polling until the
    // attempts budget runs out, exactly like the "created" status does.
    if (attemptCount < MAX_RETRY_ATTEMPTS && isTransientPollingError(error)) {
      timeoutId = setTimeout(
        () => checkOrderStatus(orderId, attemptCount + 1),
        RETRY_INTERVAL,
      );
      return;
    }

    modalHub.hideLoading();

    if (isOrderRepoError(error)) {
      // 404 — order not found (bots, expired links, etc.) — not actionable
      if (error.isNotFound()) {
        modalHub.showError(
          t("components.checkout_callback.unknown_error_title"),
          t("components.checkout_callback.order_not_found"),
          t("components.checkout_callback.unknown_error_cta"),
        );
        return;
      }

      // 401 / 403 / 429 — bots or CloudFlare blocking — not actionable, silent
      if (error.isUnauthorized() || error.isTooManyRequests()) {
        showGenericError();
        return;
      }

      // Real backend error (5xx / unknown) — report with useful context
      report(`Order polling failed: [${error.code}] ${error.message}`, {
        context: {
          cause:
            error.cause instanceof Error
              ? error.cause.message
              : String(error.cause),
          attempts: attemptCount,
        },
        tags: { "order.id": orderId },
      });
      showGenericError();
      return;
    }

    // BFF structured validation / business-rule error
    const bffMessage = errorsToString(error, "\n");
    if (bffMessage) {
      modalHub.showError(
        t("components.checkout_callback.unknown_error_title"),
        bffMessage,
        t("components.checkout_callback.unknown_error_cta"),
      );
      return;
    }

    // Truly unknown — last resort
    report("Unknown error while polling order status", {
      context: { error: JSON.stringify(error), attempts: attemptCount },
      tags: { "order.id": orderId },
    });
    showGenericError();
  }
}

onMounted(() => {
  const orderId = route.value.query[CHECKOUT_CALLBACK_PARAM] as string;
  if (!orderId) return;
  modalHub.showLoading(
    t("components.checkout_callback.confirming_order_title"),
    t("components.checkout_callback.confirming_order_message"),
  );
  checkOrderStatus(orderId);
});

onBeforeUnmount(() => {
  if (timeoutId) {
    clearTimeout(timeoutId);
    timeoutId = null;
  }
});
</script>

<template>
  <div :class="css('')"></div>
</template>

<style lang="scss" scoped>
.checkout-callback {
  width: 0;
  height: 0;
}
</style>
